
import { User } from '../types';
import { api } from './api';

const TOKEN_KEY = 'whatsapp_sender_token';

export const register = async (name: string, email: string, password: string, organizationName: string, whatsapp: string, recaptchaToken?: string): Promise<User> => {
    const { user, token } = await api.post('/auth/register', { name, email, password, organizationName, whatsapp, recaptchaToken });
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    }
    return user;
};

export const login = async (email: string, password: string, recaptchaToken?: string): Promise<User> => {
    const { user, token } = await api.post('/auth/login', { email, password, recaptchaToken });
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    }
    return user;
};

export const loginWithGoogle = async (idToken: string): Promise<User> => {
    const { user, token } = await api.post('/auth/google-signin', { idToken });
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    }
    return user;
};

export const logout = (): void => {
    localStorage.removeItem(TOKEN_KEY);
};

export const getToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
};

export const updateOrgSettings = (settings: { liveChatTimeoutMinutes?: number }): Promise<{ message: string }> => {
    return api.put('/organization/settings', settings);
};

export const verifySession = async (): Promise<User | null> => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
        return null;
    }
    try {
        const { user } = await api.get('/auth/me');
        return user;
    } catch (error) {
        console.error("Session verification failed", error);
        logout();
        return null;
    }
};

export const requestPasswordReset = async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
};

export const resetPassword = (token: string, newPassword: string): Promise<{ message: string }> => {
    return api.post('/auth/reset-password', { token, newPassword });
};

// Verify user email
export const verifyEmail = (token: string): Promise<{ message: string }> => {
    return api.post('/auth/verify-email', { token });
};

// --- App Configuration ---
export const getAppConfig = (): Promise<{
    googleClientId: string;
    paystack: {
        starterPlanCode: string;
        proPlanCode: string;
        advancedPlanCode: string;
    };
}> => {
    return api.get('/config');
};

// FIX: Add missing resendVerificationEmail function.
export const resendVerificationEmail = (): Promise<{ message: string }> => {
    // This endpoint must exist on the backend and be protected by auth middleware.
    return api.post('/auth/me/resend-verification', {});
};


// --- Google OAuth Functions ---

export const getGoogleAuthUrl = (): Promise<{ url: string }> => {
    // Pass the frontend's origin to the backend so it can construct the correct redirect URI.
    const frontendOrigin = window.location.origin;
    // This is the correct, consolidated endpoint.
    return api.post('/auth/google-url', { frontendOrigin });
};

export const sendGoogleAuthCode = (code: string): Promise<{ message: string }> => {
    // Also pass the frontend's origin for the token exchange step.
    const frontendOrigin = window.location.origin;
    return api.post('/auth/google-callback', { code, frontendOrigin });
};

export const disconnectGoogleAccount = (): Promise<{ message: string }> => {
    return api.post('/auth/google-disconnect', {});
};

// --- Meta Credentials Management ---

export const saveMetaCredentials = (metaToken: string, phoneId: string, wabaId: string, metaAppSecret: string): Promise<{ message: string }> => {
    return api.post('/auth/me/credentials', { metaToken, phoneId, wabaId, metaAppSecret });
};

export const disconnectMetaCredentials = (): Promise<{ message: string }> => {
    return api.delete('/auth/me/credentials');
};

export const regenerateWebhookToken = (): Promise<{ newWebhookToken: string }> => {
    return api.post('/auth/me/regenerate-webhook-token', {});
};

// --- Voiceflow Credentials Management ---
export const saveVoiceflowCredentials = (apiKey: string, projectId: string, versionId: string): Promise<{ message: string }> => {
    return api.post('/voiceflow/integrations', { apiKey, projectId, versionId });
};

export const disconnectVoiceflowCredentials = (): Promise<{ message: string }> => {
    return api.post('/voiceflow/integrations/disconnect', {});
};
